import React from "react";
import type { ImageCaptureFieldLabels } from "../../../../../../../shared/frontend/components/ImageCaptureField";
import type { I18nContextValue } from "../../../app/i18n/useI18n";

/** Obergrenze für ein per Kamera aufgenommenes Foto (500 KB). */
const PHOTO_CAPTURE_MAX_BYTES = 500_000;

type PersonPhotoControllerArgs = {
  api: {
    blob: (url: string) => Promise<Blob>;
    req: (url: string, options: RequestInit) => Promise<unknown>;
  };
  person: { hasPhoto?: boolean | null; updatedAt?: string | null } | null | undefined;
  personId: string;
  photoApiBasePath?: "persons" | "users";
  load: () => Promise<unknown>;
  message: { error: (content: React.ReactNode) => void; success: (content: React.ReactNode) => void };
  text: I18nContextValue["text"];
};

/** Produktadapter: authentifiziertes Laden/Speichern; Kamera und Darstellung besitzt ImageCaptureField. */
export function usePersonPhotoController({ api, person, personId, photoApiBasePath = "persons", load, message, text }: PersonPhotoControllerArgs) {
  const photoKey = `${photoApiBasePath}:${personId}:${person?.updatedAt ?? ""}`;
  const [photo, setPhoto] = React.useState<{ key: string; url: string } | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const uploadInFlight = React.useRef(false);

  React.useEffect(() => {
    if (!person?.hasPhoto) return;
    // Geschützte Fotos brauchen Bearer-Header; ein img-src kann sie nicht mitsenden.
    let alive = true;
    let objectUrl: string | null = null;
    void (async () => {
      try {
        const version = encodeURIComponent(String(person.updatedAt || ""));
        const blob = await api.blob(`/api/v1/${photoApiBasePath}/${encodeURIComponent(personId)}/photo?v=${version}`);
        if (!alive) return;
        objectUrl = URL.createObjectURL(blob);
        setPhoto({ key: photoKey, url: objectUrl });
      } catch {
        if (alive) setPhoto(null);
      }
    })();
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [api, person?.hasPhoto, person?.updatedAt, personId, photoApiBasePath, photoKey]);

  const handleFileUpload = React.useCallback(async (file: File) => {
    if (uploadInFlight.current) return false;
    uploadInFlight.current = true;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      await api.req(`/api/v1/${photoApiBasePath}/${encodeURIComponent(personId)}/photo`, { method: "PUT", body, headers: {} });
      message.success(text("Photo saved."));
      await load();
      return true;
    } catch {
      // Der zentrale API-Fehlerpfad meldet den Fehler; die Kameravorschau bleibt erhalten.
      return false;
    } finally {
      uploadInFlight.current = false;
      setUploading(false);
    }
  }, [api, personId, photoApiBasePath, load, message, text]);

  const labels: ImageCaptureFieldLabels = {
    empty: text("No image"),
    upload: text("Upload image"),
    takePhoto: text("Take photo"),
    retake: text("Retake"),
    usePhoto: text("Save"),
    cancel: text("Cancel"),
    previewAlt: text("Person profile photo enlarged alt"),
    cameraSecureContextRequired: text("Camera only available over HTTPS or localhost. Use file upload as fallback."),
    cameraUnsupported: text("Camera is not supported by the browser. Please use file upload."),
    cameraPermissionDenied: text("Camera access was denied. Please allow camera permission in your browser."),
    cameraNotFound: text("No camera found."),
    cameraUnavailable: text("Camera access is not available."),
    cameraNotReady: text("Camera is not ready yet. Please wait a moment."),
  };

  return {
    photoObjectUrl: person?.hasPhoto && photo?.key === photoKey ? photo.url : null,
    uploading,
    handleFileUpload,
    labels,
    onError: message.error,
    captureLimit: { maxBytes: PHOTO_CAPTURE_MAX_BYTES, tooLargeMessage: text("Image is too large (max 500KB). Please move closer or use file upload.") },
  };
}
